import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/pro-light-svg-icons/faCoins';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../components/primitives/Panel';
import { NativeUpgradeNote } from '../../components/NativeUpgradeNote/NativeUpgradeNote';
import { TeamOwnerNote } from '../../../team/TeamOwnerNote';
import { useWorkspaceOwner } from '../../../team/useWorkspaceOwner';
import type { BrowseUsageData } from '../hooks/useBrowseUsage';
import { plansWithMoreCredits } from '../browseFormat';
import { browseCreditMessages as messages } from '../browseMessages';
import { BrowseCreditsMeter } from './BrowseCreditsMeter';
import { BrowseUpgradePath } from './BrowseUpgradePath';
import { BrowseTrialOffer } from './BrowseTrialOffer';
import { useBrowseUpgradeAction } from './useBrowseUpgradeAction';
import { useResetCountdown } from './useResetCountdown';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRail.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseCreditsUsedUpProps {
  usage: BrowseUsageData;
}

/**
 * The credits are used up (design b5 owner, b6 member and native app). Search still works; a row
 * that costs a credit says "No credits". Label shows only the reset time.
 */
const BrowseCreditsUsedUpComponent: React.FC<BrowseCreditsUsedUpProps> = ({ usage }) => {
  const intl = useIntl();
  const owner = useWorkspaceOwner();
  const countdown = useResetCountdown(usage.resetsAt);
  const hasNext = plansWithMoreCredits(usage.plan).length > 0;
  const paidPlan = usage.plan !== 'free' && usage.plan !== 'trial';
  const { mode, openPlans, portalUrl } = useBrowseUpgradeAction({ isOwner: usage.isOwner, paidPlan, enabled: hasNext });

  let path: React.ReactNode = null;
  if (hasNext && mode === 'native') {
    path = <NativeUpgradeNote />;
  } else if (hasNext && mode === 'member') {
    path = <TeamOwnerNote owner={owner} />;
  } else if (hasNext) {
    path = <BrowseUpgradePath plan={usage.plan} portalUrl={portalUrl ?? null} openPlans={openPlans} />;
  }

  return (
    <Panel as="section" tone="brand" data-testid="browse-credits-used-up">
      <PanelHead
        title={intl.formatMessage(messages.title)}
        icon={<FontAwesomeIcon icon={faCoins} />}
        right={intl.formatMessage(messages.usedOf, { used: usage.used, limit: usage.limit })}
      />
      <PanelBody stack>
        <div className={styles.usedUpHead}>
          <h3 className={styles.usedUpTitle}>{intl.formatMessage(messages.usedUpTitle, { limit: usage.limit })}</h3>
          <p className={styles.usedUpText}>{intl.formatMessage(messages.usedUpBody)}</p>
          {countdown !== null && (
            <span className={styles.ticks}>{intl.formatMessage(messages.newCreditsIn, { time: countdown })}</span>
          )}
        </div>
        <BrowseCreditsMeter left={0} limit={usage.limit} />
        {usage.trialAvailable && mode === 'owner' && <BrowseTrialOffer />}
        {path}
      </PanelBody>
      {mode === 'owner' && hasNext && (
        <PanelFoot>
          <span className={styles.footLine}>
            <span>{intl.formatMessage(messages.followTip)}</span>
            <button type="button" className={styles.linkButton} onClick={openPlans}>
              {intl.formatMessage(messages.comparePlans)}
            </button>
          </span>
        </PanelFoot>
      )}
    </Panel>
  );
};

export const BrowseCreditsUsedUp = TypedMemo(BrowseCreditsUsedUpComponent);
